import Backbone from "backbone";

import CustomDataField from "./custom_data_field";
import FieldValuePair from "./field_value_pair";
import CustomDataFields from "../collections/custom_data_fields";

import ExceptionHandling from "shared/logging/exception_handling";

import Config from "legacy/config";

import Model from "backdraft-app/src/model";

import _ from "underscore";

export default class CustomData extends Model {
  url() {
    return this.get("customDataClassApi");
  }

  initialize(data) {
    super.initialize(data);

    if (!this.get('payloadParams')) {
      throw new Error("payloadParams is required and must be an object");
    }

    // Single Use Modal:
    //   - Fetch custom data if modal contains data_owner_id, set 'merge' rule to false.
    //   - Should get the inherited and primary custom data hash via the customDataClassApi URL.
    // Bulk Use Modal:
    //   - Do not fetch custom data if modal contains data_owner_ids. Set 'merge' rule to true.
    //   - Should not get the inherited and primary custom data hash because we do not show those on the bulk case.
    if (this.get('payloadParams').data_owner_id) {
      this.set("merge", false);
      this.deferredCustomData = this.fetch({ url: this.get("customDataClassApi"), data: this.get("payloadParams") });
    } else {
      this.set("merge", true);
    }

    this.deferredFields = this.fetch({ url: this.get("customDataFieldsApiUrl") });
    this.set('selectedPartnerNames', new Backbone.Model({ partnerName: [] }));
    this.set('customFieldValuePairs', new Backbone.Model({ fieldValues: {} }));
    this.set('friendlyModalClassName', this.getFriendlyNameFromClass(this.get('className')));
    this.set('modalClassName', this.get('className'));
  }

  getFieldData(fieldPartnerName) {
    return this.get('owner_custom_data')[fieldPartnerName] || {};
  }

  getDataType(partnerName) {
    return this._getFieldByPartnerName(partnerName).data_type;
  }

  getFieldCount() {
    return this.get("recordsTotal");
  }

  _getFieldByPartnerName(partnerName) {
    return _.findWhere(this.get('data'), { partner_name: partnerName }) || {
      friendly_name: "",
      partner_name: "",
      data_type: ""
    };
  }

  toJSON(options) {
    return _.extend({}, this.get('payloadParams'), { merge: this.get('merge') }, this.get('collection').toJSON());
  }

  getFriendlyNameFromClass(className) {
    const classHierarchyMapping = {
      Network: "Network",
      Advertiser: Config.localization.advertiserNameFromNetwork.toString(false, false, false, true),
      AdvertiserCampaign: "Campaign",
      Affiliate: Config.localization.affiliateNameFromNetwork.toString(false, false, false, true),
      AffiliateCampaign: `${Config.localization.affiliateNameFromNetwork.toString(false, false, false, true)} Campaign`,
      VirtualLine: "Promo Number"
    };

    return classHierarchyMapping[className] || ExceptionHandling.logError(`Invalid class name: ${className}`);
  }

  _getFieldValuePairs() {
    const fields = {};
    _.each(this.get("data"), ({ partner_name: partnerName, field_values: fieldValues }) => {
      fields[partnerName] = new FieldValuePair({
        partnerName,
        fieldValues
      });
    });
    return fields;
  }

  emptyFieldModel() {
    const init = {
      fieldPartnerName: "",
      fieldFriendlyName: "",
      fieldValue: "",
      fieldSourceClassName: this.get("className"),
      friendlySourceClassName: this.get('friendlyModalClassName'),
      inherited: false,
      dataType: this.getDataType(),
      modalReference: this
    };
    return new CustomDataField(init);
  }

  buildCollection() {
    this.get('customFieldValuePairs').set('fieldValues', this._getFieldValuePairs());
    const mergedCustomData = _.extend({}, this.get('inherited_custom_data'), this.get("owner_custom_data"));
    const fieldModels = _.map(mergedCustomData, (field, partnerName) => {
      let inherited;

      if (field.data_owner_type !== (this.get('className'))) {
        inherited = true;
      } else {
        inherited = false;
        this.get("selectedPartnerNames").get('partnerName').push(partnerName);
      }

      this._addCurrentValueToFieldValues(partnerName, field);

      const fieldFromDB = _.find(this.get("data"), ({ partner_name: name }) => name === partnerName);

      const init = {
        fieldPartnerName: partnerName,
        fieldFriendlyName: fieldFromDB ? fieldFromDB.friendly_name : '',
        fieldValue: field.value,
        fieldSourceClassName: field.data_owner_type,
        friendlySourceClassName: this.getFriendlyNameFromClass(field.data_owner_type),
        inherited,
        dataType: this.getDataType(partnerName),
        modalReference: this
      };

      return new CustomDataField(init);
    });

    if (fieldModels.length === 0 && fieldModels.length < this.getFieldCount()) {
      fieldModels.push(this.emptyFieldModel());
    }

    const collection = new CustomDataFields(fieldModels);

    const sortedArray = _.sortBy(collection.models, model => {
      const classHierarchyOrder = {
        Network: "1",
        Advertiser: "2",
        AdvertiserCampaign: "3",
        Affiliate: "4",
        AffiliateCampaign: "5",
        VirtualLine: "6"
      };

      const emptyField = model.get('fieldFriendlyName') === "" ? "7" : "";

      // Sort by hierarchy followed by friendly name.
      const sortKey = emptyField + classHierarchyOrder[model.get('fieldSourceClassName')] + model.get("fieldFriendlyName");

      return sortKey;
    });

    collection.reset();
    _.each(sortedArray, model => {
      collection.add(model);
    });
    this.set('collection', collection);
    return collection;
  }

  // Adds currently applied value if it's not stored in the saved field list for the customer data field
  _addCurrentValueToFieldValues(partnerName, field) {
    const fieldValuePair = this.get('customFieldValuePairs').get('fieldValues')[partnerName];
    if (fieldValuePair) {
      const fieldValues = fieldValuePair.get("fieldValues");
      if (fieldValues && fieldValues.length > 0 && !_.any(fieldValues, value => value === field.value)) {
        fieldValues.unshift(field.value);
      }
    }
  }
}
